import React, { Fragment, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    CircularProgress,
    Menu,
    MenuItem,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { ExpandMoreOutlined, MoreVertOutlined } from '@mui/icons-material';
import Box from '@mui/material/Box';
import { map } from 'lodash';
import GetImageLogoContainer from '@/components/common/get-image-logo-container/get-image-logo-container';
import extractIdFromURI from '@/utils/id';
import HideImageIcon from '@mui/icons-material/HideImage';
import { v4 as uuid } from 'uuid';

interface Props {
    id: string;
    name: string | React.ReactNode;
    children: any;
    pl?: number;
    flexDirection?: string;
    selected?: boolean;
    defaultExpanded?: boolean;
    onSelect?: () => void;
    menuItems: {
        label: string;
        icon: any;
        onClick: any;
        test?: string;
        disabled?: boolean;
    }[];
    withLogo?: boolean;
}

const AccordionItem = ({
    name,
    id,
    selected = false,
    children,
    pl = 1,
    flexDirection = 'row-reverse',
    onSelect,
    defaultExpanded = false,
    menuItems,
    withLogo = false,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [accordionState, setAccordionState] = useState<boolean>(defaultExpanded);
    const [hasBeenToggled, setHasBeenToggled] = useState<boolean>(false);

    useEffect(() => {
        // Solo sincronizar con defaultExpanded si el usuario no ha interactuado con el accordion
        if (!hasBeenToggled) {
            setAccordionState(defaultExpanded);
        }
    }, [defaultExpanded, hasBeenToggled]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleChangeAccordionToggle = (event: React.SyntheticEvent, isExpanded: boolean) => {
        // Solo permitir toggle desde el icono de expandir
        if ((event.target as HTMLElement).closest('[data-cy="expand-icon"]') || 
            (event.target as HTMLElement).closest('.MuiAccordionSummary-expandIconWrapper')) {
            setAccordionState(isExpanded);
            setHasBeenToggled(true);
        }
    };

    const handleNameClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        // Si está cerrado, abrirlo
        if (!accordionState) {
            setAccordionState(true);
            setHasBeenToggled(true);
        }
        // Siempre navegar/marcar
        onSelect?.();
    };

    return (
        <Fragment>
            <Accordion
                expanded={accordionState}
                disableGutters
                sx={{
                    borderRadius: '0 !important',
                    boxShadow: 0,
                    '&::before': {
                        bgcolor: 'white',
                    },
                }}
                onChange={handleChangeAccordionToggle}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreOutlined data-cy={'expand-icon'} />}
                    sx={{
                        p: 0,
                        pl,
                        minHeight: 0,
                        flexDirection,
                        gap: 1,
                        '& .MuiAccordionSummary-content': {
                            m: 0,
                        },
                        ...(selected && {
                            backgroundColor: (theme: any) => theme.palette.custom.selected,
                        }),
                    }}
                >
                    <Box
                        onClick={handleNameClick}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: withLogo ? 'auto 1fr auto' : '1fr auto',
                            alignItems: 'center',
                            gap: 1,
                            flexGrow: 1,
                        }}
                    >
                        {withLogo && (
                            <GetImageLogoContainer avatarId={extractIdFromURI(id) as string}>
                                {({ logoUrl, loadingLogo }: { logoUrl: string | null; loadingLogo: boolean }) => (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            placeItems: 'center',
                                            placeContent: 'center',
                                            width: 25,
                                            height: 25,
                                        }}
                                    >
                                        {loadingLogo ? (
                                            <CircularProgress size={20} />
                                        ) : logoUrl ? (
                                            <Box
                                                component='img'
                                                src={logoUrl}
                                                alt=''
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        ) : (
                                            <HideImageIcon
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </GetImageLogoContainer>
                        )}

                        {typeof name === 'string' ? (
                            <Typography justifySelf={'start'} fontSize={14}>
                                {name}
                            </Typography>
                        ) : (
                            name
                        )}

                        <Box>
                            <Box
                                id={`${withLogo ? 'client' : 'site'}-menu-button`}
                                onClick={handleClick}
                                sx={{
                                    borderRadius: 2,
                                    p: 0.5,
                                    m: 0.5,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'action.active',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            >
                                <MoreVertOutlined />
                            </Box>
                        </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails
                    sx={{
                        p: 0,
                    }}
                >
                    {children}
                </AccordionDetails>
            </Accordion>

            {open && (
                <Menu
                    id='client-menu'
                    aria-labelledby={`${withLogo ? 'client' : 'site'}-menu-button`}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    sx={{
                        '& .MuiMenu-list': {
                            p: 0,
                            display: 'grid',
                        },
                        '& .MuiMenuItem-root': {
                            p: 0.5,
                            pr: 1,
                        },
                    }}
                >
                    {map(menuItems, ({ label, onClick, icon, test, disabled }) => (
                        <MenuItem
                            key={uuid()}
                            onClick={() => {
                                handleClose();
                                onClick();
                            }}
                            data-cy={test}
                            disabled={disabled}
                        >
                            <Box display={'flex'} gap={1} color={(theme: any) => theme.palette.primary.main}>
                                {icon}

                                <Typography variant='button'>
                                    <b>{label}</b>
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </Fragment>
    );
};

export default AccordionItem;
